/* eslint-disable */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";
import useHttp from "../../hooks/use-http";
import toast from "react-hot-toast";
import Spinner from "../Spinner";
import {
  getCachedTransferInfo,
  setCachedTransferInfo,
  getCachedTransferUrl,
  setCachedTransferUrl,
  clearTransferCache,
} from "../../utils/transferStorage";

const copyToClipboard = (textToCopy) => {
  navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      toast.success("Copied to clipboard!");
    })
    .catch((err) => {
      toast.error("Unable to copy to clipboard. Please try again.");
      console.error("Copy to clipboard failed:", err);
    });
};

const fetchedStatusUrls = new Set();
const fetchedInitTransfers = new Set();

const PayByTransfer = () => {
  const outletContext = useOutletContext() || {};
  const { transactionInfo } = outletContext;
  const [transferInfo, setTransferInfo] = useState(() => getCachedTransferInfo(window.location.pathname.split("/").filter(Boolean).pop()) || {});
  const [time, setTime] = useState(null);
  const [url, setUrl] = useState(() => getCachedTransferUrl(window.location.pathname.split("/").filter(Boolean).pop()) || "");
  const [error] = useState("");
  // eslint-disable-next-line
  const [statusMessage, setStatusMessage] = useState("");
  const [isExpired, setIsExpired] = useState(false);

  // Sandbox simulation state
  const [simulationType, setSimulationType] = useState("approved");

  const navigate = useNavigate();
  const { isLoading: detailsLoading, sendRequest: initiateTransfer } =
    useHttp();
  const { sendRequest: checkStatus, isLoading: checkLoading } = useHttp();
  const { sendRequest: fetchUrl } = useHttp();
  const [isLoading, setIsLoading] = useState(false);

  const { id } = useParams();
  useTitle("Transfer");

  const isTestMode =
    transactionInfo?.data &&
    (transactionInfo.data.isLive === false || transactionInfo.data.isLive === "false");

  const isTransactionPending = useRef(false);

  const getUrl = useCallback((data) => {
    const returnUrl = data?.data?.returnUrl;
    if (returnUrl) {
      setUrl(returnUrl);
      setCachedTransferUrl(id, returnUrl);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const cachedUrl = getCachedTransferUrl(id);
    if (cachedUrl) {
      setUrl(cachedUrl);
      return;
    }
    if (fetchedStatusUrls.has(id)) return;
    fetchedStatusUrls.add(id);
    fetchUrl(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/paybytransfer/status/${id}`,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      getUrl
    );
  }, [getUrl, fetchUrl, id]);

  const convertStringToTime = (timeString) => {
    if (!timeString) return null;
    const [timeVal, period] = timeString.split(" ");
    if (!timeVal || !period) return null;

    const [hours, minutes] = timeVal.split(":");
    if (!hours || !minutes) return null;

    let newHours = parseInt(hours);
    if (period === "PM" && newHours !== 12) {
      newHours += 12;
    }

    const timeObject = new Date();
    timeObject.setHours(newHours, parseInt(minutes), 0, 0);
    return timeObject;
  };

  const calculateTimeDifference = useCallback(() => {
    const currentTime = new Date();
    const expirationTime = convertStringToTime(transferInfo?.data?.time);

    if (expirationTime) {
      const timeDiff = expirationTime - currentTime;
      if (timeDiff > 0) {
        setTime(formatTime(timeDiff));
      } else {
        setTime("00:00");
      }
    }
  }, [transferInfo?.data?.time]);

  const formatTime = (timeInMilliseconds) => {
    const minutes = Math.floor(timeInMilliseconds / (1000 * 60));
    const seconds = Math.floor((timeInMilliseconds % (1000 * 60)) / 1000);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (time === "00:00") {
      setIsExpired(true);
      setStatusMessage(`Payment Window Expired. If debited, please contact support with Reference ID: ${id}`);
      const redirectTimer = setTimeout(() => {
        if (url) {
          window.location.href = url;
        }
      }, 15000);
      return () => clearTimeout(redirectTimer);
    }
  }, [time, url, id]);

  const handleCopyToClipboard = (textToCopy) => {
    copyToClipboard(textToCopy);
  };

  useEffect(() => {
    calculateTimeDifference();
    const interval = setInterval(() => {
      calculateTimeDifference();
    }, 1000);
    return () => clearInterval(interval);
  }, [calculateTimeDifference]);

  const getTransferInfo = useCallback((data) => {
    if (data) {
      setTransferInfo(data);
      if (data?.data || data?.status === "SUCCESS") {
        setCachedTransferInfo(id, data);
      }
    }
  }, [id]);

  const transferPayment = useCallback(() => {
    initiateTransfer(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payByTransfer/initialize/${id}`,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      getTransferInfo
    );
  }, [initiateTransfer, id, getTransferInfo]);

  useEffect(() => {
    if (!id) return;
    const cachedTransfer = getCachedTransferInfo(id);
    if (cachedTransfer) {
      setTransferInfo(cachedTransfer);
      return;
    }
    if (fetchedInitTransfers.has(id)) return;
    fetchedInitTransfers.add(id);
    transferPayment();
  }, [transferPayment, id]);

  const getSimulationAmount = () => {
    switch (simulationType) {
      case "declined":
        return 1.0;
      case "insufficient":
        return 0.05;
      case "timeout":
        return 2.0;
      case "fraud":
        return 500001;
      case "approved":
      default:
        return transactionInfo?.data?.amount || 5000;
    }
  };

  const getStatus = () => {
    setIsLoading(true);
    checkStatus(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/sandbox/checkout/${id}/transfer/confirm`,
        method: "POST",
        body: {
          amount: getSimulationAmount(),
        },
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      confirmStatus
    );
  };

  const confirmStatus = (data) => {
    const status = data?.data?.status;
    if (status === "PENDING" || status === "Account detail not found") {
      setIsLoading(true);
      if (!isTransactionPending.current) {
        isTransactionPending.current = true;
        toast.success("Transaction is pending");
      }
      setTimeout(getStatus, 30000);
    } else {
      setIsLoading(false);
      if (status) {
        clearTransferCache(id);
        navigate(`/transfer/${id}/payment-confirmation/${id}/sandbox-pay`);
      } else {
        toast.error(data?.message || "Transaction status unknown. Retrying...");
        setTimeout(getStatus, 30000);
      }
    }
  };

  const currentCurrency = transactionInfo?.data?.currency || "NGN";
  const formattedCurrencySymbol = currentCurrency === "NGN" ? "₦" : currentCurrency;
  const shoppingCartAmount = transactionInfo?.data?.amount || 0;
  const processingFeeAmount = transactionInfo?.data?.fee || 0;
  const totalAmount = shoppingCartAmount + processingFeeAmount;

  if (detailsLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 font-semibold py-20 px-4">
        {error}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[560px] mx-auto py-2 px-4 sm:px-0">
      {/* Test Transaction Ribbon */}
      {isTestMode && (
        <div
          id="test-transaction-ribbon"
          data-testid="test-transaction-ribbon"
          className="w-full mb-5"
        >
          <div className="w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white text-xs sm:text-sm font-bold py-2.5 px-4 rounded-lg shadow-sm border border-red-700/30 flex items-center justify-center gap-2 uppercase tracking-wider text-center">
            <svg
              className="w-4 h-4 flex-shrink-0 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>TEST TRANSACTION</span>
          </div>
        </div>
      )}

      <div className="text-center px-4 mb-4">
        <h3 className="text-lg font-bold text-slate-800 leading-snug mb-1">
          Proceed to your bank app to complete transaction
        </h3>
      </div>

      <div className="space-y-6">
        {/* Sandbox Simulation Options */}
        <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg">
          <span className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-3 text-center">
            Select a simulation flow
          </span>
          <div className="flex flex-col gap-2">
            {[
              { id: "approved", label: "Simulate Success", desc: "Standard transfer approval", badge: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
              { id: "declined", label: "Simulate Decline", desc: "Test decline trigger (N1.00)", badge: "Declined", color: "bg-rose-50 text-rose-700 border-rose-100" },
              { id: "insufficient", label: "Insufficient Funds", desc: "Insufficient funds trigger (N0.05)", badge: "Failed", color: "bg-amber-50 text-amber-700 border-amber-100" },
              { id: "timeout", label: "Simulate Timeout", desc: "Timeout simulation (N2.00)", badge: "Timeout", color: "bg-blue-50 text-blue-700 border-blue-100" },
              { id: "fraud", label: "Simulate Fraud Block", desc: "Fraud block rule (>N500k)", badge: "Blocked", color: "bg-red-50 text-red-700 border-red-100" },
            ].map((opt) => (
              <div
                key={opt.id}
                onClick={() => setSimulationType(opt.id)}
                className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${simulationType === opt.id
                  ? "border-[#00aeef] bg-sky-50/40 shadow-sm"
                  : "border-slate-100 hover:border-slate-200 bg-white"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${simulationType === opt.id ? "border-[#00aeef]" : "border-slate-300"
                    }`}>
                    {simulationType === opt.id && <div className="w-2 h-2 rounded-full bg-[#00aeef]" />}
                  </div>
                  <div className="flex flex-col text-left">
                    <span className="text-xs font-bold text-slate-800">{opt.label}</span>
                    <span className="text-[10px] text-slate-400 mt-0.5">{opt.desc}</span>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${opt.color}`}>
                  {opt.badge}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <button
            className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            onClick={getStatus}
            disabled={isLoading || checkLoading || isExpired}
          >
            {isLoading || checkLoading ? (
              <Spinner />
            ) : (
              `I've transferred ${formattedCurrencySymbol} ${totalAmount.toLocaleString()} to the account`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayByTransfer;